import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Search, HelpCircle, BookOpen, Play, CheckCircle2, ArrowRight,
  LayoutDashboard, Store, Globe, ShoppingCart, Package, Zap,
  Webhook, Code, Database, ArrowLeftRight, Layers, Sparkles,
  DollarSign, AlertTriangle, Activity, FileText, Settings,
  Languages, Plus, Minus, Image, Truck, BarChart3, Clock
} from "lucide-react";

const HELP_SECTIONS = [
  {
    id: "distribution-preview",
    title: "Mağaza Dağıtımı",
    icon: ArrowLeftRight,
    color: "bg-blue-500",
    description: "Antioch / Vamos ayarları, ürün dağıtımı, stok, asorti ve iş kuyruğu",
    steps: [
      { title: "Nereden açılır?", content: "Mağazalar > Mağaza Dağıtımı menüsünü açın. Kaynak/hedef mağazaları, Nebim bağlantılarını ve ortam değişkeni adlarını seçin. Önce modülü etkin ve deneme modunu açık tutarak tek ürün işi çalıştırın." },
      { title: "Mevcut servisler", content: "Mevcut stok/fiyat, sipariş ve asorti ayarları değiştirilmez. Eski stok/fiyat seçimi açık olan hedefte yeni canlı dağıtım engellenir. Yeni modül ayrı iş kuyruğu ve isteğe bağlı işleyici kullanır." },
      { title: "Kararlar ne anlama gelir?", content: "Yazılacak: boş veya uygulamaya ait alan. Korunacak: elle/başka servisle girilmiş değer. Sahiplenilecek: ilk geçişte Nebim ile aynı değer. Değişmedi: aynı değer. Örnek önizleme ve deneme Shopify'a yazmaz; canlı işte başarılı yazma sonrası sahiplik kaydı tutulur." },
      { title: "Ayrıntılı rehber", content: "Yönetim ekranındaki Kullanım ve canlı geçiş rehberi bağlantısını açın. Sunucu kurulumu, alan eşleştirmeleri, mağaza kuralları, kuyruk, webhook, pilot, geri dönüş ve sorun giderme açıklanır." }
    ],
    tips: ["Başlangıçta modül kapalı, deneme modu açıktır", "Deneme işleri dış servis okuyabilir; DeepL kullanım tüketebilir", "Canlı geçişte hedeflerin eski servis kapsamını ayırın", "Dağıtımı durdur düğmesi yeni yazmaları durdurur"]
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    color: "bg-blue-500",
    description: "Genel bakış ve performans özeti",
    steps: [
      {
        title: "Dashboard Nedir?",
        content: "Dashboard, mağazanızın genel performansını tek bakışta görmenizi sağlar. Satış, sipariş, stok ve ürün istatistiklerinizi buradan takip edebilirsiniz."
      },
      {
        title: "İstatistik Kartları",
        content: "Üst kısımdaki kartlar size toplam satış, sipariş sayısı, ürün sayısı ve stok uyarılarını gösterir. Her karttaki yüzdelik değişim geçen haftaya göre durumu belirtir."
      },
      {
        title: "Satış Trendi Grafiği",
        content: "Son 7 günlük satış trendini çizgi grafik olarak gösterir. Minimum, ortalama ve maksimum satış değerleri altında belirtilir."
      },
      {
        title: "Sipariş Durumu",
        content: "Sağ taraftaki pasta grafik, siparişlerinizin durumlarını gösterir: Tamamlanan (yeşil), Bekleyen (sarı), Sorunlu (kırmızı)."
      },
      {
        title: "En Çok Satan Ürünler",
        content: "En popüler ürünlerinizi ve toplam satış tutarlarını listeler."
      },
      {
        title: "Düşük Stok Uyarıları",
        content: "Stok seviyesi kritik olan ürünleri gösterir. Bu ürünler için hızlıca stok ekleyebilirsiniz."
      }
    ],
    tips: [
      "Dashboard'u günlük olarak kontrol edin",
      "Düşük stok uyarılarını dikkate alın",
      "Yenile butonuyla verileri güncelleyin"
    ]
  },
  {
    id: "stores",
    title: "Mağazalar",
    icon: Store,
    color: "bg-green-500",
    description: "Shopify mağaza yönetimi",
    steps: [
      {
        title: "Mağaza Ekleme",
        content: "Sağ üstteki 'Mağaza Ekle' butonuna tıklayın. Shopify mağaza URL'nizi (örn: mystore.myshopify.com) ve Admin API Access Token'ınızı girin."
      },
      {
        title: "Access Token Alma",
        content: "Shopify Admin > Settings > Apps and sales channels > Develop apps > Create an app > Configure Admin API scopes > Install app adımlarını izleyin."
      },
      {
        title: "Mağaza Aktif/Pasif",
        content: "Her mağaza kartındaki toggle ile mağazayı aktif veya pasif yapabilirsiniz. Pasif mağazalar senkronizasyona dahil edilmez."
      },
      {
        title: "Bağlantı Testi",
        content: "'Bağlantıyı Test Et' butonu ile Shopify bağlantınızın çalışıp çalışmadığını kontrol edin."
      },
      {
        title: "Mağaza Silme",
        content: "Mağaza kartındaki çöp kutusu ikonuna tıklayarak mağazayı silebilirsiniz. Bu işlem geri alınamaz."
      }
    ],
    tips: [
      "Birden fazla mağaza bağlayabilirsiniz",
      "Access Token'ınızı güvenli tutun",
      "Bağlantı hatası alırsanız token'ı yenileyin"
    ]
  },
  {
    id: "marketplaces",
    title: "Pazaryerleri",
    icon: Globe,
    color: "bg-purple-500",
    description: "Pazaryeri entegrasyonları",
    steps: [
      {
        title: "Pazaryerleri Sayfası",
        content: "Trendyol, HepsiBurada, N11, Çiçeksepeti, Amazon, NOON ve NAMSHI pazaryerlerini yönetebilirsiniz."
      },
      {
        title: "Pazaryeri Bağlama",
        content: "İlgili pazaryeri sekmesine tıklayın, API bilgilerinizi (Seller ID, API Key, Secret) girin ve 'Kaydet' butonuna basın."
      },
      {
        title: "Ayar Alanları",
        content: "Her pazaryeri için farklı ayar alanları mevcuttur: Ürün adı alanı, fiyat tipi, barkod alanı, stok alanı, komisyon ayarları vb."
      },
      {
        title: "Aktif/Pasif Durumu",
        content: "Pazaryerini aktif veya pasif yaparak sipariş çekme ve ürün gönderme işlemlerini kontrol edebilirsiniz."
      }
    ],
    tips: [
      "Her pazaryerinin farklı API gereksinimleri var",
      "API bilgilerini ilgili pazaryeri panelinden alın",
      "Test modunda başlayıp canlıya geçin"
    ]
  },
  {
    id: "marketplace-orders",
    title: "Pazaryeri Siparişleri",
    icon: ShoppingCart,
    color: "bg-orange-500",
    description: "Pazaryerlerinden sipariş çekme ve Shopify'a aktarma",
    steps: [
      {
        title: "Siparişleri Çekme",
        content: "'Siparişleri Çek' butonuna tıklayarak tüm bağlı pazaryerlerinden yeni siparişleri çekin."
      },
      {
        title: "Otomatik Çekme (Zamanlayıcı)",
        content: "'Zamanlayıcı' butonuna tıklayın. Çekme aralığını seçin (5-60 dakika). 'Aktif' konumuna getirin ve 'Kaydet' butonuna basın."
      },
      {
        title: "Shopify'a Aktarma",
        content: "Aktarmak istediğiniz siparişleri checkbox ile seçin. 'Shopify'a Aktar' butonuna tıklayın. Seçili siparişler Shopify'a sipariş olarak oluşturulur."
      },
      {
        title: "Sipariş Durumu Güncelleme",
        content: "Sipariş satırındaki '...' menüsüne tıklayın. Yeni durumu seçin (Beklemede, Hazırlanıyor, Kargoda, Teslim Edildi, İptal, İade). Kargo bilgilerini girin ve kaydedin."
      },
      {
        title: "Filtreleme",
        content: "Siparişleri pazaryerine, duruma veya aktarım durumuna göre filtreleyebilirsiniz. Arama kutusuyla sipariş no veya müşteri adı arayabilirsiniz."
      },
      {
        title: "Toplu İşlemler",
        content: "'Tümünü Seç' ile tüm siparişleri seçip toplu aktarım yapabilirsiniz."
      }
    ],
    tips: [
      "Zamanlayıcıyı aktif edin, siparişler otomatik gelsin",
      "Aktarılmamış siparişleri düzenli kontrol edin",
      "Kargo bilgilerini doğru girdiğinizden emin olun"
    ]
  },
  {
    id: "products",
    title: "Ürün Yönetimi",
    icon: Package,
    color: "bg-cyan-500",
    description: "Ürün listeleme ve senkronizasyon",
    steps: [
      {
        title: "Ürün Listesi",
        content: "Nebim'den çekilen tüm ürünlerinizi bu sayfada görüntüleyebilirsiniz. Ürün kodu, adı, renk, beden, stok ve fiyat bilgileri listelenir."
      },
      {
        title: "Ürün Arama",
        content: "Arama kutusuna ürün kodu veya adı yazarak filtreleme yapabilirsiniz."
      },
      {
        title: "Shopify'a Gönderme",
        content: "Göndermek istediğiniz ürünleri seçin ve 'Shopify'a Gönder' butonuna tıklayın. Ürünler varyantlarıyla birlikte Shopify'a aktarılır."
      },
      {
        title: "Stok/Fiyat Güncelleme",
        content: "'Stok & Fiyat Güncelle' butonu ile mevcut ürünlerin stok ve fiyatlarını Nebim'den çekerek Shopify'da günceller."
      },
      {
        title: "Ürün Detayı",
        content: "Ürün satırına tıklayarak detay sayfasına gidin. Tüm varyantları, görselleri ve özellikleri görüntüleyin."
      }
    ],
    tips: [
      "Önce küçük bir ürün grubuyla test edin",
      "Stok/fiyat güncellemesini düzenli yapın",
      "Hatalı ürünleri kontrol edin"
    ]
  },
  {
    id: "auto-sync",
    title: "Otomatik Sync",
    icon: Zap,
    color: "bg-yellow-500",
    description: "Otomatik stok ve fiyat senkronizasyonu",
    steps: [
      {
        title: "Otomatik Sync Nedir?",
        content: "Nebim'deki stok ve fiyat değişikliklerini belirlediğiniz aralıklarla otomatik olarak Shopify'a aktarır."
      },
      {
        title: "Sync Aktifleştirme",
        content: "'Otomatik Sync' toggle'ını açık konuma getirin. Sync aralığını seçin (5, 10, 15, 30, 60 dakika). Ayarları kaydedin."
      },
      {
        title: "Sync Geçmişi",
        content: "Son yapılan sync işlemlerini, tarihlerini ve sonuçlarını görebilirsiniz."
      },
      {
        title: "Manuel Sync",
        content: "'Şimdi Sync Et' butonu ile bekleme süresi olmadan hemen sync başlatabilirsiniz."
      },
      {
        title: "Hata Durumları",
        content: "Sync sırasında oluşan hatalar loglanır. 'Log Yönetimi' sayfasından detayları görebilirsiniz."
      }
    ],
    tips: [
      "Yoğun dönemlerde sync aralığını kısaltın",
      "İlk sync'te tüm ürünler güncellenir",
      "Hata alırsanız logları kontrol edin"
    ]
  },
  {
    id: "translation",
    title: "Dil & Çeviri",
    icon: Languages,
    color: "bg-indigo-500",
    description: "Ürün verilerini farklı dillere çevirme",
    steps: [
      {
        title: "Ürün Çevirisi Sekmesi",
        content: "Varsayılan olarak 'Ürün Çevirisi' sekmesi açılır. Bu sekme ile ürün verilerinizi toplu olarak çevirebilirsiniz."
      },
      {
        title: "Çevrilecek Alanlar",
        content: "Checkbox'lardan çevirmek istediğiniz alanları seçin: Ürün Adları, Renkler, Kategoriler, Özellikler, Açıklamalar."
      },
      {
        title: "Hedef Dil Seçimi",
        content: "'Hedef Dil' dropdown'undan çevirmek istediğiniz dili seçin: İngilizce, Arapça, Almanca, Fransızca, İspanyolca, Rusça, Çince, Japonca, Korece."
      },
      {
        title: "Ürün Seçimi",
        content: "Çevirmek istediğiniz ürünleri checkbox ile seçin veya 'Tümünü Seç' butonunu kullanın."
      },
      {
        title: "Çeviri İşlemi",
        content: "'Seçilenleri Çevir' butonuna tıklayın. Gemini AI çeviriyi gerçekleştirir ve sonuçlar tabloda gösterilir."
      },
      {
        title: "Sonuçları Kopyalama",
        content: "Çeviri sonuçlarını JSON formatında kopyalayabilir veya tek tek kopyalama yapabilirsiniz."
      }
    ],
    tips: [
      "Önce birkaç ürünle test edin",
      "Çevirileri manuel kontrol edin",
      "Teknik terimleri kontrol edin"
    ]
  },
  {
    id: "orders",
    title: "Sipariş Yönetimi",
    icon: ShoppingCart,
    color: "bg-rose-500",
    description: "Shopify siparişlerini yönetme",
    steps: [
      {
        title: "Sipariş Listesi",
        content: "Shopify'dan gelen tüm siparişler burada listelenir. Sipariş no, müşteri, tutar, durum ve tarih bilgileri görüntülenir."
      },
      {
        title: "Nebim'e Aktarma",
        content: "Seçili siparişleri 'Nebim'e Aktar' butonu ile Nebim ERP'ye aktarabilirsiniz."
      },
      {
        title: "Sipariş Detayı",
        content: "Sipariş satırına tıklayarak ürün detaylarını, müşteri bilgilerini ve kargo adresini görüntüleyin."
      },
      {
        title: "Durum Filtreleme",
        content: "Bekleyen, İşleniyor, Gönderildi, Tamamlandı, İptal durumlarına göre filtreleme yapın."
      }
    ],
    tips: [
      "Siparişleri günlük olarak Nebim'e aktarın",
      "Bekleyen siparişleri öncelikli işleyin",
      "Hatalı aktarımları loglardan kontrol edin"
    ]
  },
  {
    id: "nebim",
    title: "Nebim Hareketleri",
    icon: Activity,
    color: "bg-emerald-500",
    description: "Nebim ERP işlemleri",
    steps: [
      {
        title: "Stok Ekle",
        content: "Ürün kodunu girin, eklemek istediğiniz miktarı yazın, depo seçin ve 'Stok Ekle' butonuna basın."
      },
      {
        title: "Stok Çıkar",
        content: "Ürün kodunu girin, çıkarmak istediğiniz miktarı yazın, depo seçin ve işlemi onaylayın."
      },
      {
        title: "Ürün Aç",
        content: "Yeni ürün kartı oluşturmak için gerekli bilgileri doldurun: Ürün kodu, adı, kategori, marka, fiyat vb."
      },
      {
        title: "Fotoğraf Ekle",
        content: "Ürün kodunu girin, fotoğrafları yükleyin. Sıralama için sürükle-bırak kullanın."
      },
      {
        title: "Fiyat Değiştir",
        content: "Ürün kodunu girin, yeni fiyatı yazın, fiyat tipini seçin (satış fiyatı, liste fiyatı) ve kaydedin."
      },
      {
        title: "Mağaza Sevk",
        content: "Ürünleri bir mağazadan diğerine sevk etmek için kaynak/hedef depo ve ürün listesini belirleyin."
      },
      {
        title: "Raporlar",
        content: "Satış raporu, stok raporu, hareket raporu gibi Nebim raporlarına erişin."
      }
    ],
    tips: [
      "Stok işlemlerini dikkatli yapın",
      "Her işlem Nebim'e anında yansır",
      "Hatalı işlem için Nebim'den düzeltme yapın"
    ]
  },
  {
    id: "settings",
    title: "Ayarlar",
    icon: Settings,
    color: "bg-slate-500",
    description: "Sistem ve bağlantı ayarları",
    steps: [
      {
        title: "Nebim Bağlantı Ayarları",
        content: "Nebim API URL'si, kullanıcı adı, şifre ve şirket kodunu girin. 'Bağlantıyı Test Et' ile kontrol edin."
      },
      {
        title: "Varsayılan Değerler",
        content: "Varsayılan depo kodu, ofis kodu, dil ve barkod tipi ayarlayın."
      },
      {
        title: "Sipariş Ayarları",
        content: "Sipariş aktarımı için POS terminal, ödeme kodları, kargo tanımları ve e-arşiv ayarlarını yapılandırın."
      },
      {
        title: "Otomatik Transfer",
        content: "Sipariş, stok, iptal/iade ve yeni ürün transferi için otomatik aktarım ayarlarını belirleyin."
      }
    ],
    tips: [
      "Bağlantı bilgilerini doğru girin",
      "Değişikliklerden önce mevcut ayarları not alın",
      "Test ortamında deneyin"
    ]
  },
  {
    id: "logs",
    title: "Log Yönetimi",
    icon: FileText,
    color: "bg-gray-500",
    description: "Sistem logları ve hata takibi",
    steps: [
      {
        title: "Log Listesi",
        content: "Tüm sistem işlemleri burada loglanır: Sync, aktarım, hata, uyarı mesajları."
      },
      {
        title: "Filtreleme",
        content: "Log tipine (başarılı, hata, uyarı, bilgi), modüle ve tarihe göre filtreleme yapın."
      },
      {
        title: "Detay Görüntüleme",
        content: "Log satırına tıklayarak detaylı hata mesajını ve stack trace'i görüntüleyin."
      },
      {
        title: "Log Temizleme",
        content: "Eski logları temizlemek için tarih aralığı seçip 'Temizle' butonuna basın."
      }
    ],
    tips: [
      "Hata loglarını düzenli kontrol edin",
      "Tekrarlayan hataları inceleyin",
      "Kritik hataları hemen çözün"
    ]
  }
];

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredSections = HELP_SECTIONS.filter(section => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        section.title.toLowerCase().includes(query) ||
        section.description.toLowerCase().includes(query) ||
        section.steps.some(step => 
          step.title.toLowerCase().includes(query) || 
          step.content.toLowerCase().includes(query)
        )
      );
    }
    return true;
  });

  return (
    <div className="space-y-6 fade-in" data-testid="help-page">
      {/* Header */}
      <div className="text-center py-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl text-white">
        <div className="flex justify-center mb-4">
          <div className="p-4 bg-white/20 rounded-full">
            <BookOpen className="w-12 h-12" />
          </div>
        </div>
        <h1 className="text-4xl font-bold mb-2">Yardım Merkezi</h1>
        <p className="text-blue-100 text-lg">Shopify-Nebim Entegratör kullanım rehberi</p>
        
        {/* Search */}
        <div className="max-w-xl mx-auto mt-6 px-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nasıl yapılır? Arama yapın..."
              className="pl-12 py-6 text-lg bg-white text-slate-900 border-0 shadow-lg"
            />
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {HELP_SECTIONS.slice(0, 6).map((section) => {
          const Icon = section.icon;
          return (
            <Card 
              key={section.id} 
              className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-1"
              onClick={() => {
                document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <CardContent className="p-4 text-center">
                <div className={`w-12 h-12 ${section.color} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <p className="font-medium text-sm">{section.title}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help Sections */}
      <div className="space-y-6">
        {filteredSections.map((section) => {
          const Icon = section.icon;
          return (
            <Card key={section.id} id={section.id} className="scroll-mt-4">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${section.color} rounded-xl flex items-center justify-center`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{section.title}</CardTitle>
                    <CardDescription className="text-base">{section.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {section.steps.map((step, index) => (
                    <AccordionItem key={index} value={`step-${index}`}>
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-bold text-slate-600">
                            {index + 1}
                          </div>
                          <span className="font-medium">{step.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pl-11 text-slate-600 leading-relaxed">
                        {step.content}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* Tips */}
                {section.tips && section.tips.length > 0 && (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      İpuçları
                    </h4>
                    <ul className="space-y-1">
                      {section.tips.map((tip, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-amber-700">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No Results */}
      {filteredSections.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <HelpCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-600 mb-2">Sonuç bulunamadı</h3>
            <p className="text-slate-500">"{searchQuery}" ile eşleşen yardım konusu bulunamadı.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setSearchQuery("")}
            >
              Aramayı Temizle
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Contact Support */}
      <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <CardContent className="py-8 text-center">
          <h3 className="text-2xl font-bold mb-2">Hala yardıma mı ihtiyacınız var?</h3>
          <p className="text-slate-300 mb-4">Aradığınızı bulamadıysanız bizimle iletişime geçin</p>
          <div className="flex justify-center gap-4">
            <Button variant="secondary">
              <FileText className="w-4 h-4 mr-2" />
              Destek Talebi Oluştur
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
